// admin/src/pages/ManageTripDetails.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import adminApi from '../services/adminApi';
import './ManageTripDetails.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');

const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) return path;
  return `${SERVER_URL}${path}`;
};

/* ═══════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════ */

const ICON_OPTIONS = [
  { value: 'mountain', label: 'Mountain' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'temple', label: 'Temple' },
  { value: 'sunrise', label: 'Sunrise' },
  { value: 'water', label: 'Water' },
  { value: 'landmark', label: 'Landmark' },
];

const CATEGORY_OPTIONS = [
  'Mountain', 'Beach', 'Adventure', 'Cultural', 'Wildlife',
  'Nature', 'Hill Station', 'Waterfall', 'General',
];

const BOOKING_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'coming_soon', label: 'Coming Soon' },
  { value: 'sold_out', label: 'Sold Out' },
  { value: 'closed', label: 'Booking Closed' },
];

const emptyTrip = {
  tripId: '', title: '', subtitle: '', location: '', state: '', destination: '',
  category: 'General', tags: [], difficulty: 'Easy', duration: '', durationLabel: '',
  nights: '', days: '', price: '', originalPrice: '', rating: '', reviewCount: '',
  maxPeople: 20, minAge: 5, joinedPeople: 0, startPoint: '', endPoint: '',
  bestSeason: '', image: '', gallery: [], profiles: [], description: '',
  inclusionTags: [], inclusions: [], exclusions: [], itinerary: [],
  highlights: [], faqs: [], notes: [], reviews: [], routeWaypoints: [], 
  isActive: true,
  bookingStatus: 'open',
  isLimitedOffer: false, offerImage: '',
};

const emptyItineraryDay = {
  day: 1, title: '', description: '', meals: [], overnight: '', highlights: [], altitude: '', stops: [],
};

const emptyHighlight = { title: '', description: '', icon: 'landmark' };
const emptyFaq = { question: '', answer: '' };
const emptyReview = { name: '', avatar: '', rating: 5, date: '', title: '', text: '', tripDate: '' };

/* ── SVG Tab Icons ── */
const TabIcons = {
  basic: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  pricing: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  images: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  description: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="17" y1="10" x2="3" y2="10" />
      <line x1="21" y1="6" x2="3" y2="6" />
      <line x1="21" y1="14" x2="3" y2="14" />
      <line x1="17" y1="18" x2="3" y2="18" />
    </svg>
  ),
  inclusions: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  itinerary: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  highlights: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  faqs: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  notes: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="9" y1="9" x2="15" y2="9" />
      <line x1="9" y1="13" x2="13" y2="13" />
    </svg>
  ),
  route: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  ),
  reviews: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
};

const FORM_SECTIONS = [
  { key: 'basic', label: 'Basic Info' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'images', label: 'Images' },
  { key: 'description', label: 'Description' },
  { key: 'inclusions', label: 'Inc / Exc' },
  { key: 'itinerary', label: 'Itinerary' },
  { key: 'highlights', label: 'Highlights' },
  { key: 'faqs', label: 'FAQs' },
  { key: 'notes', label: 'Notes' },
  { key: 'route', label: 'Route Map' },
  { key: 'reviews', label: 'Reviews' },
];

const FIELD_SECTION_MAP = {
  title: 'basic',
  subtitle: 'basic',
  location: 'basic',
  nights: 'basic',
  days: 'basic',
  price: 'pricing',
  originalPrice: 'pricing',
  image: 'images',
  description: 'description',
};

/* ═══════════════════════════════════════
   CUSTOM SELECT — replaces native <select> so the
   open dropdown matches the app's own styling instead
   of the browser/OS default popup
   ═══════════════════════════════════════ */

function CustomSelect({ value, onChange, options, placeholder = 'Select...' }) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);

  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const openMenu = () => {
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  };

  const handleSelect = (optValue) => {
    onChange(optValue);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!open) openMenu();
      else if (highlightedIndex >= 0) handleSelect(options[highlightedIndex].value);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) openMenu();
      else setHighlightedIndex((prev) => Math.min(prev + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (open) setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  return (
    <div className="mtd-select" ref={wrapperRef}>
      <button
        type="button"
        className={`mtd-select__trigger${open ? ' mtd-select__trigger--open' : ''}`}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="mtd-select__value">
          {selectedOption ? selectedOption.label : <span className="mtd-select__placeholder">{placeholder}</span>}
        </span>
        <svg className={`mtd-select__arrow${open ? ' mtd-select__arrow--open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <ul className="mtd-select__menu" role="listbox">
          {options.map((opt, i) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`mtd-select__item${opt.value === value ? ' mtd-select__item--active' : ''}${i === highlightedIndex ? ' mtd-select__item--highlighted' : ''}`}
              onClick={() => handleSelect(opt.value)}
              onMouseEnter={() => setHighlightedIndex(i)}
            >
              <span className="mtd-select__item-label">{opt.label}</span>
              {opt.value === value && (
                <svg className="mtd-select__check" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */

function ManageTripDetails() {
  // ── List State ──
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ── Form State ──
  const [showForm, setShowForm] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [formData, setFormData] = useState({ ...emptyTrip });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');

  // ── Image State ──
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState('');
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [offerImageFile, setOfferImageFile] = useState(null);
  const [offerImagePreview, setOfferImagePreview] = useState('');

  // ── Review Avatar State ──
  const [reviewAvatarFiles, setReviewAvatarFiles] = useState({});
  const [reviewAvatarPreviews, setReviewAvatarPreviews] = useState({});

  // ✅ Raw string state for comma-separated fields
  const [rawCommaFields, setRawCommaFields] = useState({
    tags: '',
    inclusionTags: '',
  });

  // ✅ Raw string state for itinerary comma fields (keyed as `${dayIndex}_meals`, `${dayIndex}_highlights`)
  const [rawItineraryFields, setRawItineraryFields] = useState({});

  // Raw string for the combined "lat, lng" input per waypoint (keyed by index)
  const [rawWaypointCoords, setRawWaypointCoords] = useState({});
  // Index of the waypoint card currently being dragged for reordering
  const [dragWaypointIndex, setDragWaypointIndex] = useState(null);

  // ── Fetch trips ──
  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getTrips();
      setTrips(data.trips || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch trips');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error && !showForm) {
      const t = setTimeout(() => setError(''), 8000);
      return () => clearTimeout(t);
    }
  }, [error, showForm]);

  const filteredTrips = trips.filter((trip) => {
    const q = searchQuery.toLowerCase();
    return (
      trip.title?.toLowerCase().includes(q) ||
      trip.location?.toLowerCase().includes(q) ||
      trip.destination?.toLowerCase().includes(q) ||
      trip.category?.toLowerCase().includes(q) ||
      String(trip.tripId).includes(q)
    );
  });

  /* ═══════════════════════════════════════
     FORM HANDLERS
     ═══════════════════════════════════════ */

  const resetForm = () => {
    setShowForm(false);
    setEditingTrip(null);
    setFormData({ ...emptyTrip });
    setMainImageFile(null);
    setMainImagePreview('');
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setOfferImageFile(null);
    setOfferImagePreview('');
    setReviewAvatarFiles({});
    setReviewAvatarPreviews({});
    setRawCommaFields({ tags: '', inclusionTags: '' });
    setRawItineraryFields({});
    setRawWaypointCoords({});
    setDragWaypointIndex(null);
    setActiveSection('basic');
    setError('');
  };

  const handleNewTrip = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    setFormData({
      tripId: trip.tripId || '',
      title: trip.title || '',
      subtitle: trip.subtitle || '',
      location: trip.location || '',
      state: trip.state || '',
      destination: trip.destination || '',
      category: trip.category || 'General',
      tags: trip.tags || [],
      difficulty: trip.difficulty || 'Easy',
      duration: trip.duration || '',
      durationLabel: trip.durationLabel || '',
      nights: trip.nights || '',
      days: trip.days || '',
      price: trip.price || '',
      originalPrice: trip.originalPrice || '',
      rating: trip.rating || '',
      reviewCount: trip.reviewCount || '',
      maxPeople: trip.maxPeople || 20,
      minAge: trip.minAge || 5,
      joinedPeople: trip.joinedPeople || 0,
      startPoint: trip.startPoint || '',
      endPoint: trip.endPoint || '',
      bestSeason: trip.bestSeason || '',
      image: trip.image || '',
      gallery: trip.gallery || [],
      profiles: trip.profiles || [],
      description: trip.description || '',
      inclusionTags: trip.inclusionTags || [],
      inclusions: trip.inclusions || [],
      exclusions: trip.exclusions || [],
      itinerary: trip.itinerary || [],
      highlights: trip.highlights || [],
      faqs: trip.faqs || [],
      notes: trip.notes || [],
      reviews: (trip.reviews || []).filter((r) => !r.reviewId),
      routeWaypoints: trip.routeWaypoints || [],
      isActive: trip.isActive !== false,
      bookingStatus: ['coming_soon', 'sold_out', 'closed'].includes(trip.bookingStatus) ? trip.bookingStatus : 'open',
      isLimitedOffer: trip.isLimitedOffer || false,
      offerImage: trip.offerImage || '',
    });
    setMainImageFile(null);
    setMainImagePreview(getImageUrl(trip.image));
    setGalleryFiles([]);
    setGalleryPreviews((trip.gallery || []).map(getImageUrl));
    setOfferImageFile(null);
    setOfferImagePreview(getImageUrl(trip.offerImage));
    setReviewAvatarFiles({});
    const avatarPreviews = {};
    (trip.reviews || []).filter((r) => !r.reviewId).forEach((r, i) => {
      if (r.avatar) avatarPreviews[i] = getImageUrl(r.avatar);
    });
    setReviewAvatarPreviews(avatarPreviews);

    setRawCommaFields({
      tags: (trip.tags || []).join(', '),
      inclusionTags: (trip.inclusionTags || []).join(', '),
    });
    setRawItineraryFields({});
    setRawWaypointCoords({});

    setActiveSection('basic');
    setShowForm(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMainImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainImageFile(file);
      setMainImagePreview(URL.createObjectURL(file));
    }
  };

  const clearMainImage = () => {
    setMainImageFile(null);
    setMainImagePreview('');
    handleChange('image', '');
  };

  const handleOfferImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setOfferImageFile(file);
      setOfferImagePreview(URL.createObjectURL(file));
    }
  };

  const clearOfferImage = () => {
    setOfferImageFile(null);
    setOfferImagePreview('');
    handleChange('offerImage', '');
  };

  const handleGalleryAdd = (e) => {
    const files = Array.from(e.target.files);
    setGalleryFiles((prev) => [...prev, ...files]);
    const previews = files.map((f) => URL.createObjectURL(f));
    setGalleryPreviews((prev) => [...prev, ...previews]);
  };

  const handleGalleryRemove = (index) => {
    setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
    const existingCount = formData.gallery?.length || 0;
    if (index < existingCount) {
      setFormData((prev) => ({
        ...prev,
        gallery: prev.gallery.filter((_, i) => i !== index),
      }));
    } else {
      const fileIndex = index - existingCount;
      setGalleryFiles((prev) => prev.filter((_, i) => i !== fileIndex));
    }
  };

  // ✅ No file size limit — only validate type
  const handleReviewAvatarChange = (reviewIndex, e) => {
    const file = e.target.files[0];
    if (file) {
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowed.includes(file.type)) {
        setError('Only JPG, PNG, and WebP images are allowed for reviewer photos.');
        return;
      }
      setReviewAvatarFiles((prev) => ({ ...prev, [reviewIndex]: file }));
      setReviewAvatarPreviews((prev) => ({ ...prev, [reviewIndex]: URL.createObjectURL(file) }));
      setError('');
    }
  };

  const clearReviewAvatar = (reviewIndex) => {
    setReviewAvatarFiles((prev) => {
      const updated = { ...prev };
      delete updated[reviewIndex];
      return updated;
    });
    setReviewAvatarPreviews((prev) => {
      const updated = { ...prev };
      delete updated[reviewIndex];
      return updated;
    });
    handleObjArrayChange('reviews', reviewIndex, 'avatar', '');
  };

  const handleArrayAdd = (field, value = '') => {
    setFormData((prev) => ({ ...prev, [field]: [...(prev[field] || []), value] }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const handleArrayRemove = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleObjArrayAdd = (field, template) => {
    setFormData((prev) => ({ ...prev, [field]: [...(prev[field] || []), { ...template }] }));
  };

  const handleObjArrayChange = (field, index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? { ...item, [key]: value } : item)),
    }));
  };

  const handleObjArrayRemove = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));

    if (field === 'reviews') {
      setReviewAvatarFiles((prev) => {
        const updated = {};
        Object.entries(prev).forEach(([k, v]) => {
          const ki = Number(k);
          if (ki < index) updated[ki] = v;
          else if (ki > index) updated[ki - 1] = v;
        });
        return updated;
      });
      setReviewAvatarPreviews((prev) => {
        const updated = {};
        Object.entries(prev).forEach(([k, v]) => {
          const ki = Number(k);
          if (ki < index) updated[ki] = v;
          else if (ki > index) updated[ki - 1] = v;
        });
        return updated;
      });
    }
  };



  // ✅ FIX: Parse on every onChange — no blur dependency, no item cap.
  // Storing raw string so typing "Breakfast, " doesn't strip the trailing comma mid-type.
  const handleItineraryNestedChange = (dayIndex, nestedField, value) => {
    const key = `${dayIndex}_${nestedField}`;
    // Keep the raw string so the input feels natural while typing
    setRawItineraryFields((prev) => ({ ...prev, [key]: value }));
    // Parse and sync to formData immediately on every keystroke
    const arr = value.split(',').map((s) => s.trim()).filter(Boolean);
    setFormData((prev) => ({
      ...prev,
      itinerary: prev.itinerary.map((day, i) =>
        i === dayIndex ? { ...day, [nestedField]: arr } : day
      ),
    }));
  };

  // Returns the raw in-progress string if available, otherwise joins the stored array
  const getItineraryFieldValue = (dayIndex, nestedField, storedArray) => {
    const key = `${dayIndex}_${nestedField}`;
    return rawItineraryFields[key] !== undefined
      ? rawItineraryFields[key]
      : (storedArray || []).join(', ');
  };

  const handleCommaFieldChange = (field, value) => {
    setRawCommaFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleCommaFieldBlur = (field) => {
    const raw = rawCommaFields[field] || '';
    const arr = raw.split(',').map((s) => s.trim()).filter(Boolean);
    handleChange(field, arr);
  };

  // ─── Combined "lat, lng" input for a waypoint ───
  // Accepts the exact format Google Maps shows when you click a location,
  // e.g. "27.3389, 88.6065" — comma or plain whitespace both work.
  const handleWaypointCoordsChange = (index, value) => {
    setRawWaypointCoords((prev) => ({ ...prev, [index]: value }));
    let parts = value.split(',');
    if (parts.length < 2) parts = value.trim().split(/\s+/);
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        setFormData((prev) => ({
          ...prev,
          routeWaypoints: (prev.routeWaypoints || []).map((wp, i) =>
            i === index ? { ...wp, lat, lng } : wp
          ),
        }));
      }
    }
  };

  // Shows the raw in-progress string while typing, otherwise derives "lat, lng" from stored values
  const getWaypointCoordsValue = (index, wp) => {
    if (rawWaypointCoords[index] !== undefined) return rawWaypointCoords[index];
    if (wp.lat !== '' && wp.lng !== '' && wp.lat !== undefined && wp.lng !== undefined) {
      return `${wp.lat}, ${wp.lng}`;
    }
    return '';
  };

  const handleWaypointRemove = (index) => {
    handleObjArrayRemove('routeWaypoints', index);
    // Raw coords are keyed by index — simplest to clear and let values
    // re-derive from the already-committed formData.routeWaypoints.
    setRawWaypointCoords({});
  };

  // ─── Drag-to-reorder waypoint cards ───
  const handleWaypointDragStart = (index, e) => {
    setDragWaypointIndex(index);
    // Use the whole card as the drag image, not just the grip,
    // so it feels like the entire row is being lifted.
    const card = e.currentTarget.closest('.mtd__waypoint-card');
    if (card) {
      const rect = card.getBoundingClientRect();
      e.dataTransfer.setDragImage(card, e.clientX - rect.left, e.clientY - rect.top);
    }
  };

  const handleWaypointDragOver = (e) => e.preventDefault();

  const handleWaypointDrop = (targetIndex) => {
    if (dragWaypointIndex === null || dragWaypointIndex === targetIndex) {
      setDragWaypointIndex(null);
      return;
    }
    setFormData((prev) => {
      const list = [...(prev.routeWaypoints || [])];
      const [moved] = list.splice(dragWaypointIndex, 1);
      list.splice(targetIndex, 0, moved);
      return { ...prev, routeWaypoints: list };
    });
    setRawWaypointCoords({});
    setDragWaypointIndex(null);
  };

  const handleWaypointDragEnd = () => setDragWaypointIndex(null);

  const handleAddWaypointForDay = (day) => {
    setFormData((prev) => ({
      ...prev,
      routeWaypoints: [...(prev.routeWaypoints || []), { name: '', lat: '', lng: '', day }],
    }));
    setRawWaypointCoords({});
  };

  /* ═══════════════════════════════════════
     VALIDATION — required fields only, no count limits
     ═══════════════════════════════════════ */

  const validateForm = () => {
    const missing = [];
    if (!formData.title.trim()) missing.push({ field: 'Title', section: 'basic' });
    if (!formData.subtitle.trim()) missing.push({ field: 'Subtitle', section: 'basic' });
    if (!formData.location.trim()) missing.push({ field: 'Location', section: 'basic' });
    if (!formData.nights && formData.nights !== 0) missing.push({ field: 'Nights', section: 'basic' });
    if (!formData.days && formData.days !== 0) missing.push({ field: 'Days', section: 'basic' });
    if (!formData.price && formData.price !== 0) missing.push({ field: 'Price', section: 'pricing' });
    if (!formData.originalPrice && formData.originalPrice !== 0) missing.push({ field: 'Original Price', section: 'pricing' });
    if (!mainImageFile && !formData.image) missing.push({ field: 'Main Image', section: 'images' });
    if (!formData.description.trim()) missing.push({ field: 'Description', section: 'description' });
    return missing;
  };

  // ✅ Only validate required fields on reviews — no rating range enforcement
  const validateReviews = (reviews) => {
    const errors = [];
    reviews.forEach((review, index) => {
      if (!review.name?.trim()) errors.push(`Review #${index + 1}: Name is required`);
      if (!review.date?.trim()) errors.push(`Review #${index + 1}: Date is required`);
      if (!review.text?.trim()) errors.push(`Review #${index + 1}: Review text is required`);
    });
    return errors;
  };

  /* ═══════════════════════════════════════
     SAVE
     ═══════════════════════════════════════ */

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const missing = validateForm();
      if (missing.length > 0) {
        const fieldNames = missing.map((m) => m.field).join(', ');
        setError(`Required fields missing: ${fieldNames}`);
        setActiveSection(missing[0].section);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSaving(false);
        return;
      }

      // Sync comma fields before saving
      const finalTags = rawCommaFields.tags
        ? rawCommaFields.tags.split(',').map((s) => s.trim()).filter(Boolean)
        : formData.tags || [];
      const finalInclusionTags = rawCommaFields.inclusionTags
        ? rawCommaFields.inclusionTags.split(',').map((s) => s.trim()).filter(Boolean)
        : formData.inclusionTags || [];

      formData.tags = finalTags;
      formData.inclusionTags = finalInclusionTags;

      // ✅ No review count limit — keep all non-empty reviews
      const allReviews = formData.reviews || [];
      const keptIndices = [];
      const reviewsToSend = [];

      allReviews.forEach((review, originalIndex) => {
        if (review.name?.trim() || review.text?.trim()) {
          keptIndices.push(originalIndex);
          reviewsToSend.push(review);
        }
      });

      if (reviewsToSend.length > 0) {
        const reviewErrors = validateReviews(reviewsToSend);
        if (reviewErrors.length > 0) {
          setError(`Review validation: ${reviewErrors.join('; ')}`);
          setActiveSection('reviews');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setSaving(false);
          return;
        }
      }

      const fd = new FormData();

      const simpleFields = [
        'tripId', 'title', 'subtitle', 'location', 'state', 'destination',
        'category', 'difficulty', 'durationLabel', 'nights', 'days',
        'price', 'originalPrice', 'rating', 'reviewCount', 'maxPeople', 'minAge',
        'joinedPeople', 'startPoint', 'endPoint', 'bestSeason', 'description',
      ];
      simpleFields.forEach((field) => {
        if (formData[field] !== undefined && formData[field] !== '') {
          fd.append(field, formData[field]);
        }
      });

      const daysVal = Number(formData.days) || 0;
      fd.append('duration', formData.duration || daysVal);
      fd.append('isActive', formData.isActive);
      fd.append('bookingStatus', formData.bookingStatus);
      fd.append('isLimitedOffer', formData.isLimitedOffer);

      if (mainImageFile) {
        fd.append('image', mainImageFile);
      } else if (formData.image) {
        fd.append('imageUrl', formData.image);
      }

      if (offerImageFile) {
        fd.append('offerImage', offerImageFile);
      } else if (formData.offerImage) {
        fd.append('offerImageUrl', formData.offerImage);
      }

      const existingGallery = formData.gallery || [];
      fd.append('gallery', JSON.stringify(existingGallery));
      galleryFiles.forEach((file) => {
        fd.append('galleryImages', file);
      });

      keptIndices.forEach((originalIdx, newIdx) => {
        const file = reviewAvatarFiles[originalIdx];
        if (file) {
          fd.append(`reviewAvatar_${newIdx}`, file);
        }
      });

      const jsonFields = [
        'tags', 'profiles', 'inclusionTags', 'inclusions', 'exclusions',
        'notes', 'itinerary', 'highlights', 'faqs', 'routeWaypoints',
      ];
      jsonFields.forEach((field) => {
        fd.append(field, JSON.stringify(formData[field] || []));
      });

      fd.append('reviews', JSON.stringify(reviewsToSend));

      let result;
      if (editingTrip) {
        result = await adminApi.updateTrip(editingTrip._id, fd);
      } else {
        result = await adminApi.createTrip(fd);
      }

      setSuccess(result.message || (editingTrip ? 'Trip updated!' : 'Trip created!'));
      resetForm();
      fetchTrips();
    } catch (err) {
      console.error('Save trip error:', err);
      const serverMsg = err.response?.data?.message || '';
      const serverErrors = err.response?.data?.errors;
      const statusCode = err.response?.status || 'unknown';

      let errorMessage = '';
      if (statusCode === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (statusCode === 413) {
        errorMessage = 'File too large. Please reduce image sizes or increase server limit (client_max_body_size in Nginx).';
      } else if (statusCode === 0) {
        errorMessage = 'Cannot connect to server. Make sure the server is running.';
      } else if (serverErrors && Array.isArray(serverErrors)) {
        errorMessage = `Validation failed: ${serverErrors.join(', ')}`;
      } else if (serverMsg.includes('Review validation failed')) {
        errorMessage = serverMsg;
        setActiveSection('reviews');
      } else if (serverMsg.includes('validation failed') || serverMsg.includes('Validation failed')) {
        const fieldErrors = serverMsg.split(':').slice(1).join(':').trim();
        errorMessage = `Validation failed — ${fieldErrors}`;
      } else {
        errorMessage = serverMsg || `Failed to save trip (Status: ${statusCode}). Please check all required fields.`;
      }

      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  /* ═══════════════════════════════════════
     TOGGLE & DELETE
     ═══════════════════════════════════════ */

  const handleToggle = async (trip) => {
    try {
      const result = await adminApi.toggleTrip(trip._id);
      setSuccess(result.message);
      fetchTrips();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to toggle trip');
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await adminApi.deleteTrip(id);
      setSuccess(result.message);
      setDeleteConfirm(null);
      fetchTrips();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete trip');
    }
  };

  /* ═══════════════════════════════════════
     RENDER — FORM VIEW
     ═══════════════════════════════════════ */

  if (showForm) {
    return (
      <div className="mtd">
        {error && <div className="mtd__alert mtd__alert--error">{error}</div>}
        {success && <div className="mtd__alert mtd__alert--success">{success}</div>}

        <div className="mtd__form-header">
          <button className="mtd__back-btn" onClick={resetForm}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to Trips
          </button>
          <h1 className="mtd__form-title">
            {editingTrip ? `Edit: ${editingTrip.title}` : 'Create New Trip'}
          </h1>
        </div>

        {/* ── Section Tabs ── */}
        <div className="mtd__section-tabs">
          {FORM_SECTIONS.map((sec) => (
            <button
              key={sec.key}
              className={`mtd__section-tab ${activeSection === sec.key ? 'mtd__section-tab--active' : ''}`}
              onClick={() => setActiveSection(sec.key)}
            >
              <span className="mtd__section-tab-icon">{TabIcons[sec.key]}</span>
              <span className="mtd__section-tab-label">{sec.label}</span>
            </button>
          ))}
        </div>

        <div className="mtd__form-body">

          {/* ════════════════ BASIC INFO ════════════════ */}
          {activeSection === 'basic' && (
            <div className="mtd__section">
              <h2 className="mtd__section-title">Basic Information</h2>
              <div className="mtd__grid mtd__grid--2">
                <div className="mtd__field">
                  <label>Trip ID</label>
                  <input type="number" value={formData.tripId} onChange={(e) => handleChange('tripId', e.target.value)} placeholder="Auto-generated if empty" />
                </div>
                <div className="mtd__field">
                  <label>Title <span className="mtd__required">*</span></label>
                  <input type="text" value={formData.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="e.g., Sikkim Wonderland" />
                </div>
                <div className="mtd__field mtd__field--full">
                  <label>Subtitle <span className="mtd__required">*</span></label>
                  <input type="text" value={formData.subtitle} onChange={(e) => handleChange('subtitle', e.target.value)} placeholder="e.g., Gangtok · Pelling · Lachung" />
                </div>
                <div className="mtd__field">
                  <label>Location <span className="mtd__required">*</span></label>
                  <input type="text" value={formData.location} onChange={(e) => handleChange('location', e.target.value)} placeholder="e.g., Sikkim" />
                </div>
                <div className="mtd__field">
                  <label>State</label>
                  <input type="text" value={formData.state} onChange={(e) => handleChange('state', e.target.value)} placeholder="e.g., Sikkim" />
                </div>
                <div className="mtd__field">
                  <label>Destination</label>
                  <input type="text" value={formData.destination} onChange={(e) => handleChange('destination', e.target.value)} placeholder="e.g., Sikkim" />
                </div>
                <div className="mtd__field">
                  <label>Category</label>
                  <CustomSelect
                    value={formData.category}
                    onChange={(val) => handleChange('category', val)}
                    options={CATEGORY_OPTIONS.map((cat) => ({ value: cat, label: cat }))}
                  />
                </div>
                <div className="mtd__field">
                  <label>Nights <span className="mtd__required">*</span></label>
                  <input
                    type="number"
                    value={formData.nights}
                    onChange={(e) => {
                      const nights = e.target.value;
                      handleChange('nights', nights);
                      const days = formData.days;
                      if (nights && days) handleChange('durationLabel', `${days}D / ${nights}N`);
                    }}
                    min="0"
                  />
                </div>
                <div className="mtd__field">
                  <label>Days <span className="mtd__required">*</span></label>
                  <input
                    type="number"
                    value={formData.days}
                    onChange={(e) => {
                      const days = e.target.value;
                      handleChange('days', days);
                      const nights = formData.nights;
                      if (nights && days) handleChange('durationLabel', `${days}D / ${nights}N`);
                    }}
                    min="1"
                  />
                </div>
                <div className="mtd__field">
                  <label>Duration Label</label>
                  <input
                    type="text"
                    value={formData.durationLabel}
                    readOnly
                    placeholder="Auto-fills from Nights & Days"
                    style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="mtd__field">
                  <label>Start Point</label>
                  <input type="text" value={formData.startPoint} onChange={(e) => handleChange('startPoint', e.target.value)} />
                </div>
                <div className="mtd__field">
                  <label>End Point</label>
                  <input type="text" value={formData.endPoint} onChange={(e) => handleChange('endPoint', e.target.value)} />
                </div>
                <div className="mtd__field">
                  <label>Best Season</label>
                  <input type="text" value={formData.bestSeason} onChange={(e) => handleChange('bestSeason', e.target.value)} placeholder="e.g., March – June" />
                </div>
                <div className="mtd__field">
                  <label>Max People</label>
                  <input type="number" value={formData.maxPeople} onChange={(e) => handleChange('maxPeople', e.target.value)} min="1" />
                </div>
                <div className="mtd__field">
                  <label>Min Age</label>
                  <input type="number" value={formData.minAge} onChange={(e) => handleChange('minAge', e.target.value)} min="0" />
                </div>
              </div>

              <div className="mtd__field mtd__field--full">
                <label>Tags (comma-separated)</label>
                <input
                  type="text"
                  value={rawCommaFields.tags}
                  onChange={(e) => handleCommaFieldChange('tags', e.target.value)}
                  onBlur={() => handleCommaFieldBlur('tags')}
                  placeholder="e.g., Trending, Mountain, Best Seller"
                />
                {formData.tags?.length > 0 && (
                  <div className="mtd__comma-preview">
                    {formData.tags.map((tag, i) => (
                      <span key={i} className="mtd__comma-chip">{tag}</span>
                    ))}
                  </div>
                )}
              </div>

              <div className="mtd__grid mtd__grid--2">
                <div className="mtd__field">
                  <label className="mtd__toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                    />
                    Active (visible on website)
                  </label>
                </div>

                <div className="mtd__field">
                  <label>Booking Status</label>
                  <CustomSelect
                  value={formData.bookingStatus}
                  onChange={(val) => handleChange('bookingStatus', val)}
                  options={BOOKING_STATUS_OPTIONS}
                />
                </div>
              </div>
            </div>
          )}

          {/* ════════════════ PRICING ════════════════ */}
          {activeSection === 'pricing' && (
            <div className="mtd__section">
              <h2 className="mtd__section-title">Pricing & Stats</h2>
              <div className="mtd__grid mtd__grid--2">
                <div className="mtd__field">
                  <label>Price (₹) <span className="mtd__required">*</span></label>
                  <input type="number" value={formData.price} onChange={(e) => handleChange('price', e.target.value)} min="0" />
                </div>
                <div className="mtd__field">
                  <label>Original Price (₹) <span className="mtd__required">*</span></label>
                  <input type="number" value={formData.originalPrice} onChange={(e) => handleChange('originalPrice', e.target.value)} min="0" />
                </div>
                <div className="mtd__field">
                  <label>Rating (0–5)</label>
                  <input type="number" value={formData.rating} onChange={(e) => handleChange('rating', e.target.value)} min="0" max="5" step="0.1" />
                </div>
                <div className="mtd__field">
                  <label>Review Count</label>
                  <input type="number" value={formData.reviewCount} onChange={(e) => handleChange('reviewCount', e.target.value)} min="0" />
                </div>
                <div className="mtd__field">
                  <label>Joined People</label>
                  <input type="number" value={formData.joinedPeople} onChange={(e) => handleChange('joinedPeople', e.target.value)} min="0" />
                </div>
              </div>
              {formData.price && formData.originalPrice && Number(formData.originalPrice) > Number(formData.price) && (
                <div className="mtd__discount-preview">
                  Discount: {Math.round(((formData.originalPrice - formData.price) / formData.originalPrice) * 100)}% OFF
                  &nbsp;(Save ₹{(formData.originalPrice - formData.price).toLocaleString('en-IN')})
                </div>
              )}
            </div>
          )}

          {/* ════════════════ IMAGES ════════════════ */}
          {activeSection === 'images' && (
            <div className="mtd__section">
              <h2 className="mtd__section-title">Images</h2>

              <div className="mtd__field">
                <label>Main Image <span className="mtd__required">*</span></label>
                <div className="mtd__image-upload">
                  {mainImagePreview && (
                    <div className="mtd__image-preview">
                      <img src={mainImagePreview} alt="Main" />
                      <button className="mtd__image-remove" onClick={clearMainImage}>×</button>
                    </div>
                  )}
                  <label className="mtd__upload-btn">
                    <input type="file" accept="image/*" onChange={handleMainImageChange} hidden />
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload Image
                  </label>
                  <span className="mtd__upload-or">or</span>
                  <input
                    type="text"
                    value={formData.image || ''}
                    onChange={(e) => {
                      const url = e.target.value;
                      handleChange('image', url);
                      setMainImagePreview(url);
                    }}
                    placeholder="Paste image URL"
                    className="mtd__url-input"
                  />
                </div>
              </div>

              <div className="mtd__field">
                <label className="mtd__toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.isLimitedOffer}
                    onChange={(e) => handleChange('isLimitedOffer', e.target.checked)}
                  />
                  Show as Limited Time Offer (below "Never miss this escape" banner)
                </label>
              </div>

              {formData.isLimitedOffer && (
                <div className="mtd__field">
                  <label>Limited Time Offer Card Image</label>
                  <div className="mtd__image-upload">
                    {offerImagePreview && (
                      <div className="mtd__image-preview">
                        <img src={offerImagePreview} alt="Limited time offer" />
                        <button className="mtd__image-remove" onClick={clearOfferImage}>×</button>
                      </div>
                    )}
                    <label className="mtd__upload-btn">
                      <input type="file" accept="image/*" onChange={handleOfferImageChange} hidden />
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Upload Image
                    </label>
                    <span className="mtd__upload-or">or</span>
                    <input
                      type="text"
                      value={formData.offerImage || ''}
                      onChange={(e) => {
                        const url = e.target.value;
                        handleChange('offerImage', url);
                        setOfferImagePreview(url);
                      }}
                      placeholder="Paste image URL"
                      className="mtd__url-input"
                    />
                  </div>
                  <p className="mtd__route-tip">
                    This single image is the whole card — title, description and background
                    should already be baked into it. Only the price, "View Trip" button and the
                    "Limited time offer" badge are drawn by the site on top of it.
                  </p>
                </div>
              )}

              <div className="mtd__field">
                <label>Gallery Images</label>
                <div className="mtd__gallery-grid">
                  {galleryPreviews.map((url, i) => (
                    <div key={i} className="mtd__gallery-item">
                      <img src={url} alt={`Gallery ${i + 1}`} />
                      <button className="mtd__image-remove" onClick={() => handleGalleryRemove(i)}>×</button>
                    </div>
                  ))}
                  <label className="mtd__gallery-add">
                    <input type="file" accept="image/*" multiple onChange={handleGalleryAdd} hidden />
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    <span>Add Images</span>
                  </label>
                </div>
              </div>

              <div className="mtd__field">
                <label>Inclusion Tags (short tags for cards, comma-separated)</label>
                <input
                  type="text"
                  value={rawCommaFields.inclusionTags}
                  onChange={(e) => handleCommaFieldChange('inclusionTags', e.target.value)}
                  onBlur={() => handleCommaFieldBlur('inclusionTags')}
                  placeholder="e.g., Hotel, Meals, Sightseeing, Transport"
                />
                {formData.inclusionTags?.length > 0 && (
                  <div className="mtd__comma-preview">
                    {formData.inclusionTags.map((tag, i) => (
                      <span key={i} className="mtd__comma-chip">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════════════════ DESCRIPTION ════════════════ */}
          {activeSection === 'description' && (
            <div className="mtd__section">
              <h2 className="mtd__section-title">Description</h2>
              <div className="mtd__field">
                <label>Trip Description <span className="mtd__required">*</span></label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={8}
                  placeholder="Detailed trip description..."
                />
              </div>
            </div>
          )}

          {/* ════════════════ INCLUSIONS / EXCLUSIONS ════════════════ */}
          {activeSection === 'inclusions' && (
            <div className="mtd__section">
              <h2 className="mtd__section-title">Inclusions & Exclusions</h2>

              <div className="mtd__list-section">
                <div className="mtd__list-header">
                  <h3>Inclusions</h3>
                  <button className="mtd__add-btn" onClick={() => handleArrayAdd('inclusions', '')}>+ Add</button>
                </div>
                {(formData.inclusions || []).map((item, i) => (
                  <div key={i} className="mtd__list-item">
                    <input
                      type="text" value={item}
                      onChange={(e) => handleArrayChange('inclusions', i, e.target.value)}
                      placeholder="e.g., Accommodation in 3-star hotels"
                    />
                    <button className="mtd__remove-btn" onClick={() => handleArrayRemove('inclusions', i)}>×</button>
                  </div>
                ))}
                {(!formData.inclusions || formData.inclusions.length === 0) && (
                  <div className="mtd__empty">No inclusions added.</div>
                )}
              </div>

              <div className="mtd__list-section">
                <div className="mtd__list-header">
                  <h3>Exclusions</h3>
                  <button className="mtd__add-btn" onClick={() => handleArrayAdd('exclusions', '')}>+ Add</button>
                </div>
                {(formData.exclusions || []).map((item, i) => (
                  <div key={i} className="mtd__list-item">
                    <input
                      type="text" value={item}
                      onChange={(e) => handleArrayChange('exclusions', i, e.target.value)}
                      placeholder="e.g., Airfare / Train fare"
                    />
                    <button className="mtd__remove-btn" onClick={() => handleArrayRemove('exclusions', i)}>×</button>
                  </div>
                ))}
                {(!formData.exclusions || formData.exclusions.length === 0) && (
                  <div className="mtd__empty">No exclusions added.</div>
                )}
              </div>
            </div>
          )}

          {/* ════════════════ ITINERARY ════════════════ */}
          {activeSection === 'itinerary' && (
            <div className="mtd__section">
              <div className="mtd__list-header">
                <h2 className="mtd__section-title">Day-wise Itinerary</h2>
                <button className="mtd__add-btn" onClick={() => handleObjArrayAdd('itinerary', { ...emptyItineraryDay, day: (formData.itinerary?.length || 0) + 1 })}>
                  + Add Day
                </button>
              </div>

              {(formData.itinerary || []).map((day, i) => (
                <div key={i} className="mtd__itinerary-day">
                  <div className="mtd__itinerary-day-header">
                    <span className="mtd__day-badge">Day {day.day || i + 1}</span>
                    <button className="mtd__remove-btn" onClick={() => handleObjArrayRemove('itinerary', i)}>× Remove Day</button>
                  </div>
                  <div className="mtd__grid mtd__grid--2">
                    <div className="mtd__field">
                      <label>Day Number</label>
                      <input type="number" value={day.day} onChange={(e) => handleObjArrayChange('itinerary', i, 'day', Number(e.target.value))} min="1" />
                    </div>
                    <div className="mtd__field">
                      <label>Title <span className="mtd__required">*</span></label>
                      <input type="text" value={day.title} onChange={(e) => handleObjArrayChange('itinerary', i, 'title', e.target.value)} placeholder="e.g., Arrival in Gangtok" />
                    </div>
                  </div>
                  <div className="mtd__field">
                    <label>Description <span className="mtd__required">*</span></label>
                    <textarea value={day.description} onChange={(e) => handleObjArrayChange('itinerary', i, 'description', e.target.value)} rows={3} placeholder="Detailed day description..." />
                  </div>
                  <div className="mtd__grid mtd__grid--2">
                    <div className="mtd__field">
                      <label>Meals (comma-separated, unlimited)</label>
                      {/* ✅ Parses on every keystroke — no blur required, no item cap */}
                      <input
                        type="text"
                        value={getItineraryFieldValue(i, 'meals', day.meals)}
                        onChange={(e) => handleItineraryNestedChange(i, 'meals', e.target.value)}
                        placeholder="Breakfast, Lunch, Dinner, Snacks, Evening Tea"
                      />
                    </div>
                    <div className="mtd__field">
                      <label>Overnight Stay</label>
                      <input type="text" value={day.overnight || ''} onChange={(e) => handleObjArrayChange('itinerary', i, 'overnight', e.target.value)} placeholder="Hotel in Gangtok" />
                    </div>
                    <div className="mtd__field">
                      <label>Altitude</label>
                      <input type="text" value={day.altitude || ''} onChange={(e) => handleObjArrayChange('itinerary', i, 'altitude', e.target.value)} placeholder="5,410 ft" />
                    </div>
                    <div className="mtd__field">
                      <label>Key Stops (comma-separated, unlimited)</label>
                      {/* ✅ Parses on every keystroke — no blur required, no item cap */}
                      <input
                        type="text"
                        value={getItineraryFieldValue(i, 'highlights', day.highlights)}
                        onChange={(e) => handleItineraryNestedChange(i, 'highlights', e.target.value)}
                        placeholder="MG Marg, Teesta River, Rumtek Monastery"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {(!formData.itinerary || formData.itinerary.length === 0) && (
                <div className="mtd__empty">No itinerary days added yet. Click "+ Add Day" to start.</div>
              )}
            </div>
          )}

          {/* ════════════════ HIGHLIGHTS ════════════════ */}
          {activeSection === 'highlights' && (
            <div className="mtd__section">
              <div className="mtd__list-header">
                <h2 className="mtd__section-title">Destination Highlights</h2>
                <button className="mtd__add-btn" onClick={() => handleObjArrayAdd('highlights', { ...emptyHighlight })}>+ Add Highlight</button>
              </div>

              {(formData.highlights || []).map((h, i) => (
                <div key={i} className="mtd__highlight-item">
                  <div className="mtd__grid mtd__grid--3">
                    <div className="mtd__field">
                      <label>Title</label>
                      <input type="text" value={h.title} onChange={(e) => handleObjArrayChange('highlights', i, 'title', e.target.value)} placeholder="e.g., Yumthang Valley" />
                    </div>
                    <div className="mtd__field">
                      <label>Icon</label>
                      <CustomSelect
                        value={h.icon}
                        onChange={(val) => handleObjArrayChange('highlights', i, 'icon', val)}
                        options={ICON_OPTIONS}
                      />
                    </div>
                    <div className="mtd__field">
                      <label>Description</label>
                      <div className="mtd__input-row">
                        <input type="text" value={h.description} onChange={(e) => handleObjArrayChange('highlights', i, 'description', e.target.value)} placeholder="Short description..." />
                        <button className="mtd__remove-btn" onClick={() => handleObjArrayRemove('highlights', i)}>×</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {(!formData.highlights || formData.highlights.length === 0) && (
                <div className="mtd__empty">No highlights added yet.</div>
              )}
            </div>
          )}

          {/* ════════════════ FAQS ════════════════ */}
          {activeSection === 'faqs' && (
            <div className="mtd__section">
              <div className="mtd__list-header">
                <h2 className="mtd__section-title">Frequently Asked Questions</h2>
                <button className="mtd__add-btn" onClick={() => handleObjArrayAdd('faqs', { ...emptyFaq })}>+ Add FAQ</button>
              </div>

              {(formData.faqs || []).map((faq, i) => (
                <div key={i} className="mtd__faq-item">
                  <div className="mtd__field">
                    <label>Question {i + 1}</label>
                    <div className="mtd__input-row">
                      <input type="text" value={faq.question} onChange={(e) => handleObjArrayChange('faqs', i, 'question', e.target.value)} placeholder="e.g., Is this trip suitable for senior citizens?" />
                      <button className="mtd__remove-btn" onClick={() => handleObjArrayRemove('faqs', i)}>×</button>
                    </div>
                  </div>
                  <div className="mtd__field">
                    <label>Answer</label>
                    <textarea value={faq.answer} onChange={(e) => handleObjArrayChange('faqs', i, 'answer', e.target.value)} rows={2} placeholder="Answer to the question..." />
                  </div>
                </div>
              ))}

              {(!formData.faqs || formData.faqs.length === 0) && (
                <div className="mtd__empty">No FAQs added yet.</div>
              )}
            </div>
          )}

          {/* ════════════════ NOTES ════════════════ */}
          {activeSection === 'notes' && (
            <div className="mtd__section">
              <div className="mtd__list-header">
                <h2 className="mtd__section-title">Important Notes</h2>
                <button className="mtd__add-btn" onClick={() => handleArrayAdd('notes', '')}>+ Add Note</button>
              </div>

              {(formData.notes || []).map((note, i) => (
                <div key={i} className="mtd__list-item">
                  <input
                    type="text" value={note}
                    onChange={(e) => handleArrayChange('notes', i, e.target.value)}
                    placeholder="e.g., Carry warm clothing"
                  />
                  <button className="mtd__remove-btn" onClick={() => handleArrayRemove('notes', i)}>×</button>
                </div>
              ))}

              {(!formData.notes || formData.notes.length === 0) && (
                <div className="mtd__empty">No notes added yet.</div>
              )}
            </div>
          )}

          {/* ════════════════ ROUTE MAP STOPS ════════════════ */}
          {activeSection === 'route' && (
            <div className="mtd__section">
              <div style={{ marginBottom: 20 }}>
                <h2 className="mtd__section-title">Route Map Stops</h2>
                <p className="mtd__route-tip">
                  Add stops per day to build the interactive route map shown in Booking Details.
                  Open <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer">Google Maps</a>,
                  right-click any place and paste the "lat, lng" it copies into the Coordinates field.
                  Drag stops within a day to reorder them. Stop numbers (1, 2, 3…) on the map are
                  added automatically based on this order — just type the plain place name below.
                </p>
              </div>

              {Array.from(
                { length: Number(formData.days) || (formData.itinerary || []).length || 1 },
                (_, idx) => {
                  const dayNum = idx + 1;
                  const dayItinerary = (formData.itinerary || []).find(d => Number(d.day) === dayNum);
                  const dayStops = (formData.routeWaypoints || [])
                    .map((wp, globalIndex) => ({ ...wp, globalIndex }))
                    .filter(wp => Number(wp.day) === dayNum);

                  return (
                    <div key={dayNum} className="mtd__route-day-card">
                      <div className="mtd__route-day-header">
                        <span className="mtd__day-badge">Day {dayNum}</span>
                        {dayItinerary?.title && (
                          <span className="mtd__route-day-title">{dayItinerary.title}</span>
                        )}
                        <button
                          className="mtd__add-btn"
                          style={{ marginLeft: 'auto' }}
                          onClick={() => handleAddWaypointForDay(dayNum)}
                        >
                          + Add Stop
                        </button>
                      </div>

                      {dayStops.length === 0 && (
                        <div className="mtd__empty" style={{ margin: '6px 0 2px' }}>
                          No stops for Day {dayNum} yet.
                        </div>
                      )}

                      {dayStops.map(({ globalIndex, ...wp }, localIdx) => (
                        <div
                          key={globalIndex}
                          className={`mtd__waypoint-card${dragWaypointIndex === globalIndex ? ' mtd__waypoint-card--dragging' : ''}`}
                          onDragOver={handleWaypointDragOver}
                          onDrop={() => handleWaypointDrop(globalIndex)}
                        >
                          <span
                            className="mtd__waypoint-grip"
                            title="Drag to reorder"
                            draggable
                            onDragStart={(e) => handleWaypointDragStart(globalIndex, e)}
                            onDragEnd={handleWaypointDragEnd}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
                              <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
                              <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
                            </svg>
                          </span>
                          <div className="mtd__waypoint-badge">{localIdx + 1}</div>
                          <div className="mtd__waypoint-fields">
                            <div className="mtd__grid mtd__grid--2">
                              <div className="mtd__field">
                                <label>Stop Name <span className="mtd__required">*</span></label>
                                <input
                                  type="text"
                                  value={wp.name}
                                  onChange={e => handleObjArrayChange('routeWaypoints', globalIndex, 'name', e.target.value)}
                                  placeholder="e.g., Gangtok (no number needed)"
                                />
                              </div>
                              <div className="mtd__field">
                                <label>Coordinates (lat, lng) <span className="mtd__required">*</span></label>
                                <input
                                  type="text"
                                  value={getWaypointCoordsValue(globalIndex, wp)}
                                  onChange={e => handleWaypointCoordsChange(globalIndex, e.target.value)}
                                  placeholder="27.3389, 88.6065"
                                />
                              </div>
                            </div>
                          </div>
                          <button className="mtd__remove-btn" onClick={() => handleWaypointRemove(globalIndex)}>
                            × Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                }
              )}

              {(() => {
                const totalDays = Number(formData.days) || (formData.itinerary || []).length || 1;
                const orphanStops = (formData.routeWaypoints || [])
                  .map((wp, globalIndex) => ({ ...wp, globalIndex }))
                  .filter(wp => !(Number(wp.day) >= 1 && Number(wp.day) <= totalDays));
                if (orphanStops.length === 0) return null;
                return (
                  <div className="mtd__route-day-card" style={{ borderColor: '#f59e0b', background: '#fffbeb' }}>
                    <div className="mtd__route-day-header">
                      <span className="mtd__day-badge" style={{ background: '#d97706' }}>Unassigned</span>
                      <span className="mtd__route-day-title">
                        Old stops with no valid day — assign a day below or remove them
                      </span>
                    </div>
                    {orphanStops.map(({ globalIndex, ...wp }, localIdx) => (
                      <div key={globalIndex} className="mtd__waypoint-card">
                        <div className="mtd__waypoint-badge">{localIdx + 1}</div>
                        <div className="mtd__waypoint-fields">
                          <div className="mtd__grid mtd__grid--2">
                            <div className="mtd__field">
                              <label>Stop Name</label>
                              <input
                                type="text"
                                value={wp.name}
                                onChange={e => handleObjArrayChange('routeWaypoints', globalIndex, 'name', e.target.value)}
                              />
                            </div>
                            <div className="mtd__field">
                              <label>Coordinates (lat, lng)</label>
                              <input
                                type="text"
                                value={getWaypointCoordsValue(globalIndex, wp)}
                                onChange={e => handleWaypointCoordsChange(globalIndex, e.target.value)}
                              />
                            </div>
                            <div className="mtd__field">
                              <label>Assign to Day (1–{totalDays})</label>
                              <input
                                type="number"
                                min="1"
                                max={totalDays}
                                value={wp.day || ''}
                                onChange={e => handleObjArrayChange('routeWaypoints', globalIndex, 'day', e.target.value ? Number(e.target.value) : null)}
                              />
                            </div>
                          </div>
                        </div>
                        <button className="mtd__remove-btn" onClick={() => handleWaypointRemove(globalIndex)}>
                          × Remove
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {(formData.routeWaypoints || []).length >= 2 && (
                <div className="mtd__route-preview">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  {(formData.routeWaypoints || []).map((wp, i) => `${i + 1}. ${wp.name || '?'}`).join(' → ')}
                </div>
              )}
            </div>
          )}

          {/* ════════════════ REVIEWS ════════════════ */}
          {activeSection === 'reviews' && (
            <div className="mtd__section">
              <div className="mtd__list-header">
                <h2 className="mtd__section-title">Reviews</h2>
                {/* ✅ No review count limit */}
                <button
                  className="mtd__add-btn"
                  onClick={() => handleObjArrayAdd('reviews', { ...emptyReview })}
                >
                  + Add Review
                </button>
              </div>

              {editingTrip && (editingTrip.reviews || []).some((r) => r.reviewId) && (
                <div className="mtd__review-count-info" style={{ marginBottom: 12 }}>
                  {(editingTrip.reviews || []).filter((r) => r.reviewId).length} real traveller review(s)
                  are managed from <strong>Manage Reviews</strong> and are not shown here.
                  This tab is only for custom reviews you add manually.
                </div>
              )}

              {(formData.reviews || []).length > 0 && (
                <div className="mtd__review-count-info">
                  {formData.reviews.length} review{formData.reviews.length !== 1 ? 's' : ''} added
                  {Object.keys(reviewAvatarFiles).length > 0 && (
                    <span> · {Object.keys(reviewAvatarFiles).length} new photo{Object.keys(reviewAvatarFiles).length !== 1 ? 's' : ''} to upload</span>
                  )}
                </div>
              )}

              {(formData.reviews || []).map((review, i) => (
                <div key={i} className="mtd__review-item">
                  <div className="mtd__review-item-header">
                    <span>Review #{i + 1}</span>
                    <button className="mtd__remove-btn" onClick={() => handleObjArrayRemove('reviews', i)}>× Remove</button>
                  </div>

                  {/* ── Avatar Photo Upload ── */}
                  <div className="mtd__review-avatar-section">
                    <label>Reviewer Photo</label>
                    <div className="mtd__review-avatar-upload">
                      {reviewAvatarPreviews[i] ? (
                        <div className="mtd__review-avatar-preview">
                          <img src={reviewAvatarPreviews[i]} alt={`Reviewer ${i + 1}`} />
                          <button className="mtd__image-remove" onClick={() => clearReviewAvatar(i)}>×</button>
                        </div>
                      ) : (
                        <div className="mtd__review-avatar-placeholder">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                      )}
                      <label className="mtd__review-avatar-btn">
                        {/* ✅ No file size restriction */}
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={(e) => handleReviewAvatarChange(i, e)}
                          hidden
                        />
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        {reviewAvatarPreviews[i] ? 'Change Photo' : 'Upload Photo'}
                      </label>
                      {reviewAvatarFiles[i] && (
                        <span className="mtd__review-avatar-filename">
                          📎 {reviewAvatarFiles[i].name} ({(reviewAvatarFiles[i].size / 1024).toFixed(0)}KB)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mtd__grid mtd__grid--2">
                    <div className="mtd__field">
                      <label>Reviewer Name <span className="mtd__required">*</span></label>
                      <input type="text" value={review.name} onChange={(e) => handleObjArrayChange('reviews', i, 'name', e.target.value)} placeholder="John Doe" />
                    </div>
                    <div className="mtd__field">
                      <label>Rating (1–5) <span className="mtd__required">*</span></label>
                      <CustomSelect
                        value={review.rating}
                        onChange={(val) => handleObjArrayChange('reviews', i, 'rating', val)}
                        options={[5, 4, 3, 2, 1].map((r) => ({ value: r, label: `${r} Star${r > 1 ? 's' : ''}` }))}
                      />
                    </div>
                    <div className="mtd__field">
                      <label>Date <span className="mtd__required">*</span></label>
                      <input type="date" value={review.date} onChange={(e) => handleObjArrayChange('reviews', i, 'date', e.target.value)} />
                    </div>
                    <div className="mtd__field">
                      <label>Review Title</label>
                      <input type="text" value={review.title || ''} onChange={(e) => handleObjArrayChange('reviews', i, 'title', e.target.value)} placeholder="Amazing trip!" />
                    </div>
                    <div className="mtd__field">
                      <label>Trip Date</label>
                      <input type="text" value={review.tripDate || ''} onChange={(e) => handleObjArrayChange('reviews', i, 'tripDate', e.target.value)} placeholder="March 2024" />
                    </div>
                  </div>
                  <div className="mtd__field">
                    <label>Review Text <span className="mtd__required">*</span></label>
                    <textarea value={review.text} onChange={(e) => handleObjArrayChange('reviews', i, 'text', e.target.value)} rows={3} placeholder="Write the review text..." />
                  </div>
                </div>
              ))}

              {(!formData.reviews || formData.reviews.length === 0) && (
                <div className="mtd__empty">No reviews added yet.</div>
              )}
            </div>
          )}
        </div>

        {/* Save Bar */}
        <div className="mtd__save-bar">
          <button className="mtd__save-bar-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
          <button className="mtd__save-bar-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingTrip ? 'Update Trip' : 'Create Trip'}
          </button>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════
     RENDER — TRIP LIST VIEW
     ═══════════════════════════════════════ */

  return (
    <div className="mtd">
      {error && <div className="mtd__alert mtd__alert--error">{error}</div>}
      {success && <div className="mtd__alert mtd__alert--success">{success}</div>}

      <div className="mtd__header">
        <div className="mtd__header-left">
          <h1 className="mtd__page-title">Trip Details Manager</h1>
          <p className="mtd__page-subtitle">Create, edit and manage all trip content</p>
        </div>
        <button className="mtd__create-btn" onClick={handleNewTrip}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Trip
        </button>
      </div>

      <div className="mtd__search-bar">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by title, location, category, ID..."
        />
        {searchQuery && (
          <button className="mtd__search-clear" onClick={() => setSearchQuery('')}>×</button>
        )}
      </div>

      <div className="mtd__stats">
        <div className="mtd__stat">
          <span className="mtd__stat-number">{trips.length}</span>
          <span className="mtd__stat-label">Total Trips</span>
        </div>
        <div className="mtd__stat">
          <span className="mtd__stat-number mtd__stat-number--green">{trips.filter((t) => t.isActive).length}</span>
          <span className="mtd__stat-label">Active</span>
        </div>
        <div className="mtd__stat">
          <span className="mtd__stat-number mtd__stat-number--red">{trips.filter((t) => !t.isActive).length}</span>
          <span className="mtd__stat-label">Inactive</span>
        </div>
      </div>

      {loading && (
        <div className="mtd__loading">
          <div className="mtd__spinner" />
          <p>Loading trips...</p>
        </div>
      )}

      {!loading && (
        <div className="mtd__trip-list">
          {filteredTrips.length === 0 ? (
            <div className="mtd__empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <h3>{searchQuery ? 'No trips match your search' : 'No trips yet'}</h3>
              <p>{searchQuery ? 'Try a different search term' : 'Click "New Trip" to create your first trip'}</p>
            </div>
          ) : (
            filteredTrips.map((trip) => (
              <div key={trip._id} className={`mtd__trip-card ${!trip.isActive ? 'mtd__trip-card--inactive' : ''}`}>
                <div className="mtd__trip-card-image">
                  {trip.image ? (
                    <img
                      src={getImageUrl(trip.image)}
                      alt={trip.title}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="mtd__trip-card-placeholder">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                    </div>
                  )}
                  <div className={`mtd__trip-card-status ${trip.isActive ? 'mtd__trip-card-status--active' : 'mtd__trip-card-status--inactive'}`}>
                    {trip.isActive ? 'Active' : 'Inactive'}
                  </div>
                  {trip.bookingStatus && trip.bookingStatus !== 'open' && (
                    <div className="mtd__trip-card-status" style={{
                      background: trip.bookingStatus === 'sold_out' ? '#dc2626' : trip.bookingStatus === 'closed' ? '#6b7280' : '#111827',
                      color: '#fff',
                    }}>
                      {trip.bookingStatus === 'coming_soon' ? 'Coming Soon' : trip.bookingStatus === 'sold_out' ? 'Sold Out' : 'Booking Closed'}
                    </div>
                  )}
                </div>

                <div className="mtd__trip-card-body">
                  <div className="mtd__trip-card-top">
                    <div>
                      <span className="mtd__trip-card-id">#{trip.tripId}</span>
                      <h3 className="mtd__trip-card-title">{trip.title}</h3>
                      <p className="mtd__trip-card-subtitle">{trip.subtitle}</p>
                    </div>
                    <div className="mtd__trip-card-price">
                      <span className="mtd__trip-card-price-current">₹{trip.price?.toLocaleString('en-IN')}</span>
                      {trip.originalPrice > trip.price && (
                        <span className="mtd__trip-card-price-original">₹{trip.originalPrice?.toLocaleString('en-IN')}</span>
                      )}
                    </div>
                  </div>

                  <div className="mtd__trip-card-meta">
                    <span>{trip.location}{trip.state ? `, ${trip.state}` : ''}</span>
                    <span>•</span>
                    <span>{trip.days}D / {trip.nights}N</span>
                    <span>•</span>
                    <span>{trip.category}</span>
                    {trip.rating > 0 && (
                      <>
                        <span>•</span>
                        <span>⭐ {trip.rating} ({trip.reviewCount})</span>
                      </>
                    )}
                  </div>

                  <div className="mtd__trip-card-tags">
                    {trip.isLimitedOffer && (
                      <span className="mtd__trip-card-tag" style={{ background: '#fef3c7', color: '#b45309' }}>
                        ⚡ Limited Time Offer
                      </span>
                    )}
                    {(trip.tags || []).map((tag, i) => (
                      <span key={i} className="mtd__trip-card-tag">{tag}</span>
                    ))}
                  </div>

                  <div className="mtd__trip-card-info">
                    <span title="Itinerary days">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                      {trip.itinerary?.length || 0} days itinerary
                    </span>
                    <span title="Inclusions">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                      {trip.inclusions?.length || 0} inclusions
                    </span>
                    <span title="Highlights">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                      {trip.highlights?.length || 0} highlights
                    </span>
                    <span title="FAQs">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                      {trip.faqs?.length || 0} FAQs
                    </span>
                    <span title="Notes">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                      {trip.notes?.length || 0} notes
                    </span>
                    <span title="Reviews">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
                      {trip.reviews?.length || 0} reviews
                    </span>
                  </div>
                </div>

                <div className="mtd__trip-card-actions">
                  <button className="mtd__action-btn mtd__action-btn--edit" onClick={() => handleEditTrip(trip)} title="Edit trip">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>

                  <button
                    className={`mtd__action-btn ${trip.isActive ? 'mtd__action-btn--deactivate' : 'mtd__action-btn--activate'}`}
                    onClick={() => handleToggle(trip)}
                    title={trip.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {trip.isActive ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        </svg>
                        Hide
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        Show
                      </>
                    )}
                  </button>

                  <button className="mtd__action-btn mtd__action-btn--delete" onClick={() => setDeleteConfirm(trip)} title="Delete trip">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="mtd__modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="mtd__modal" onClick={(e) => e.stopPropagation()}>
            <div className="mtd__modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="mtd__modal-title">Delete Trip?</h3>
            <p className="mtd__modal-text">
              Are you sure you want to delete <strong>"{deleteConfirm.title}"</strong> (#{deleteConfirm.tripId})?
              This action cannot be undone.
            </p>
            <div className="mtd__modal-actions">
              <button className="mtd__modal-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="mtd__modal-delete" onClick={() => handleDelete(deleteConfirm._id)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageTripDetails;
